/**
 * `<NewChatModal>` — start a chat with optional routing (facelift-v3 guide
 * Phase 6.3, the "NewChat (md)" demo).
 *
 * A first-prompt textarea + a workflow select + a scope select. On start it
 * begins a fresh session and sends the first turn with the chosen `workflow` /
 * `scope` soft-routing hints (the chat turn honors them via the per-turn
 * directive — see backend agent_loop). "Auto" / "Whole corpus" send no hint
 * (today's default behavior).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components';
import { listNotebooks } from '@/api';

/** The named workflows the agent can be biased toward (backend catalogue). */
const WORKFLOWS: { id: string; label: string }[] = [
  { id: '', label: 'Auto — let Aletheia choose' },
  { id: 'synthesize_topic', label: 'Synthesize a topic' },
  { id: 'find_relationships', label: 'Find relationships' },
  { id: 'build_timeline', label: 'Build a timeline' },
  { id: 'find_quotes', label: 'Find quotes' },
  { id: 'whats_on_my_plate', label: "What's on my plate" },
  { id: 'find_contradictions', label: 'Find contradictions' },
];

interface NewChatModalProps {
  onClose: () => void;
  /** Start a fresh chat with the first prompt + optional routing hints. */
  onStart: (
    prompt: string,
    opts: { workflow: string | null; scope: string | null },
  ) => void;
}

export function NewChatModal({ onClose, onStart }: NewChatModalProps): JSX.Element {
  const [prompt, setPrompt] = useState('');
  const [workflow, setWorkflow] = useState('');
  const [notebookId, setNotebookId] = useState('');

  const notebooksQuery = useQuery({
    queryKey: ['notebooks', 'list-all'],
    queryFn: async () => (await listNotebooks({})).data,
  });
  const notebooks = notebooksQuery.data?.items ?? [];

  const trimmed = prompt.trim();
  const canStart = trimmed.length > 0;

  const start = (): void => {
    if (!canStart) return;
    const nb = notebooks.find((n) => n.notebookId === notebookId);
    onStart(trimmed, {
      workflow: workflow === '' ? null : workflow,
      scope: nb !== undefined ? `notebook: ${nb.title}` : null,
    });
  };

  return (
    <Modal
      eyebrow="New chat"
      title="Ask your corpus"
      size="md"
      onClose={onClose}
      footMeta="⌘↵ to start"
      footActions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn--cool" disabled={!canStart} onClick={start}>
            Start chat
          </button>
        </>
      }
    >
      <div className="modal-form">
        <div className="modal-form__row">
          <label className="modal-form__label" htmlFor="new-chat-prompt">
            First question
          </label>
          <textarea
            id="new-chat-prompt"
            className="modal-form__textarea"
            value={prompt}
            autoFocus
            placeholder="Ask anything about your notes…"
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                start();
              }
            }}
          />
        </div>

        <div className="modal-form__row">
          <label className="modal-form__label" htmlFor="new-chat-workflow">
            Workflow
          </label>
          <select
            id="new-chat-workflow"
            className="modal-form__select"
            value={workflow}
            onChange={(e) => setWorkflow(e.target.value)}
          >
            {WORKFLOWS.map((w) => (
              <option key={w.id} value={w.id}>
                {w.label}
              </option>
            ))}
          </select>
          <span className="modal-form__hint">
            A hint, not a hard rule — Aletheia still picks the best tools for the question.
          </span>
        </div>

        <div className="modal-form__row">
          <label className="modal-form__label" htmlFor="new-chat-scope">
            Scope
          </label>
          <select
            id="new-chat-scope"
            className="modal-form__select"
            value={notebookId}
            onChange={(e) => setNotebookId(e.target.value)}
          >
            <option value="">Whole corpus</option>
            {notebooks.map((nb) => (
              <option key={nb.notebookId} value={nb.notebookId}>
                {nb.title}
              </option>
            ))}
          </select>
        </div>
      </div>
    </Modal>
  );
}
